import {
  CreditCard,
  BarChart3,
  Settings,
  Banknote,
  ReceiptText,
  WalletCards,
  RotateCcw,
  ChartNoAxesCombined,
  CircleDollarSign,
  ListChecks,
  Layers3,
  List,
  Boxes,
  CalendarClock,
  SlidersHorizontal,
  Gavel,
  BadgePercent,
  Landmark,
  Receipt,
  MessageSquareText,
  CalendarRange,
} from "lucide-react";

/**
 * Fee Management hub — derived from schoolNavData "FEE MANAGEMENT" category.
 *
 * `url` is the OWNER-style path (as used in schoolNavData / owner routes in
 * App.js). Hub pages prepend "/staff-panel" to `url` when isPortal is true.
 *
 * `permission` is the permissionKey used in the matching
 * /staff-panel/... route's <PermissionGuard>.
 *
 * `module` is the ModuleGuard moduleCode wrapping that route.
 *
 * `permission: null` means the route isn't gated (or isn't exposed in the
 * staff portal) — owner always sees it since owner routes carry no guards
 * in App.js.
 *
 * `icon` contains the Lucide source icon. HubIcon is responsible for the
 * actual Quiet Duotone visual treatment.
 */
export const hubFeeLinks = [
  {
    sectionTitle: "Fee Collection",
    sectionIcon: CreditCard,

    links: [
      {
        label: "Collect Fee",
        url: "/fees/collect",
        desc: "Student payments",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: Banknote,
      },
      {
        label: "Receipts",
        url: "/fees/receipts",
        desc: "History & void",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: ReceiptText,
      },
      {
        label: "Refunds",
        url: "/fees/refunds",
        desc: "Withdrawals and adjustments",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: RotateCcw,
      },
    ],
  },

  {
    sectionTitle: "Fee Reports & Jobs",
    sectionIcon: BarChart3,

    links: [
      {
        label: "Fee Reports",
        url: "/fees/reports",
        desc: "Collection, defaulters, day book",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: ChartNoAxesCombined,
      },
      {
        label: "Due Fees Report",
        url: "/fees/due-report",
        desc: "Outstanding dues by student",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: CircleDollarSign,
      },

      // NOTE: schoolNavData lists "Fee Jobs" -> /fees/jobs, but no matching
      // route (owner or staff-panel) was found in App.js. Verify this page
      // exists before wiring the link, or drop it from the sidebar/hub if
      // it was never built.
      {
        label: "Fee Jobs",
        url: "/fees/jobs",
        desc: "Auto monthly due and bulk late fee",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: CalendarClock,
      },
    ],
  },

  {
    sectionTitle: "Fee Configuration",
    sectionIcon: Settings,

    links: [
      {
        label: "Fee Structures",
        url: "/fees/structures",
        desc: "Class-wise amounts",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: Layers3,
      },
      {
        label: "Fee Structure List",
        url: "/fees/structure-list",
        desc: "All fee structures",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: List,
      },
      {
        label: "Fee Heads",
        url: "/fees/heads",
        desc: "Categories & types",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: ListChecks,
      },
      {
        label: "Fee Batches",
        url: "/fees/batches",
        desc: "Generate & publish",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: Boxes,
      },
      {
        label: "Fee Scheduler",
        url: "/fees/scheduler",
        desc: "Auto monthly publish",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_VIEW",
        icon: CalendarRange,
      },
      {
        label: "Fee Settings",
        url: "/fees/settings",
        desc: "Notifications, receipt defaults, transport flag",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: SlidersHorizontal,
      },
      {
        label: "Late Fee Rules",
        url: "/fees/late-fee-rules",
        desc: "Grace period and penalties",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: Gavel,
      },
      {
        label: "Discount Rules",
        url: "/fees/discount-rules",
        desc: "Scholarships and concessions",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: BadgePercent,
      },
      {
        label: "Payment Modes",
        url: "/fees/payment-modes",
        desc: "Cash, UPI, gateway",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: WalletCards,
      },
      {
        label: "Bank Accounts",
        url: "/fees/bank-accounts",
        desc: "Settlement accounts for fee collection",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: Landmark,
      },
      {
        label: "Tax Config",
        url: "/fees/tax",
        desc: "GST / tax rules on fee heads",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: Receipt,
      },
      {
        label: "Notification Templates",
        url: "/fees/notification-templates",
        desc: "Reminder & receipt message templates",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: MessageSquareText,
      },
      {
        label: "Payment Schedules",
        url: "/fees/payment-schedules",
        desc: "Installment plans",
        module: "FEE_MANAGEMENT",
        permission: "FEE_MANAGEMENT_FULL",
        icon: CalendarRange,
      },
    ],
  },
];

export default hubFeeLinks;